import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getApiErrorMessage } from '../../api';
import AppShell from '../AppShell';
import { FileIcon, MB, TYPE_LABELS, daysWord, formatDate, formatSize } from './shareUtils';
import './Share.css';
import './Presentations.css';

const STATUS = { pending: 'Ждёт файл', sent: 'Отправлено', failed: 'Ошибка отправки' };

/** Кабинет: загруженные презентации, кто их запросил, выгрузка почт. */
function Presentations() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [requests, setRequests] = useState([]);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [drag, setDrag] = useState(false);
    const [accepted, setAccepted] = useState(false); // организатор подтвердил ответственность за файл
    const inputRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const [list, reqs] = await Promise.all([api.get('/presentations'), api.get('/presentations/requests')]);
            setData(list.data);
            setRequests(reqs.data.items);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить презентации'));
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    // Пока ИИ проверяет файл — обновляем раз в 5 секунд
    const checking = data?.items?.some((i) => i.status === 'checking');
    useEffect(() => {
        if (!checking) return undefined;
        const timer = setInterval(load, 5000);
        return () => clearInterval(timer);
    }, [checking, load]);
    useEffect(() => { document.title = 'Ваши презентации — СВОД'; }, []);

    const upload = async (file) => {
        if (!file || !data) return;
        setError('');
        if (!accepted) {
            setError('Сначала отметьте, что отвечаете за содержимое файла');
            return;
        }
        if (file.size > data.limits.max_bytes) {
            setError(`Файл больше ${Math.round(data.limits.max_bytes / MB)} МБ — сожмите презентацию или сохраните её в PDF`);
            return;
        }
        const form = new FormData();
        form.append('file', file);
        form.append('accept_responsibility', 'true');
        setUploading(true);
        setProgress(0);
        try {
            await api.post('/presentations', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
            });
            await load();
        } catch (err) {
            setError(err.response?.status === 413 && !err.response?.data?.detail
                ? 'Файл слишком большой'
                : getApiErrorMessage(err, 'Не удалось загрузить файл'));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Удалить «${item.title}»? Ссылки в уже отправленных письмах перестанут работать.`)) return;
        try {
            await api.delete(`/presentations/${item.id}`);
            load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось удалить'));
        }
    };

    const exportEmails = async () => {
        try {
            const res = await api.get('/presentations/requests/export', { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'presentation-emails.csv';
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось выгрузить'));
        }
    };

    let content;
    if (!data) {
        content = <div className="pres">{error ? <p className="pres-error" role="alert">{error}</p> : <div className="pres-loading">Загрузка…</div>}</div>;
    } else if (!data.available) {
        content = (
            <div className="pres-locked-wrap"><div className="pres-card pres-locked">
                <FileIcon />
                <h2>Поделиться презентацией</h2>
                <p>
                    Загрузите презентацию выступления — участники опроса или «Вопросов из зала» оставят почту и получат её
                    сразу после ответа. А вы увидите, сколько человек её запросили и скачали.
                </p>
                <p className="pres-muted">Доступно на тарифах «Расширенный» и «Корпоративный».</p>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/profile#tariffs')}>Выбрать тариф</button>
            </div></div>
        );
    } else {
        const { items, limits, stats } = data;
        const full = items.length >= limits.max_files;
        const shared = requests.filter((r) => r.shared).length;
        content = (
            <div className="pres">
                <div className="pres__head">
                    <h1>Ваши презентации</h1>
                    <p>
                        Участники оставляют почту после ответа на опрос или рядом с формой вопроса — и получают ссылку на файл.
                        Включается в настройках опроса или сессии «Вопросы из зала».
                    </p>
                </div>
                {error && <p className="pres-error" role="alert">{error}</p>}

                <section className="pres-card">
                    <div className="pres-card__title">
                        <h2>Файлы</h2>
                        <span className="pres-muted">{items.length} из {limits.max_files}</span>
                    </div>
                    {items.length > 0 && (
                        <ul className="pres-files">
                            {items.map((item) => (
                                <li key={item.id} className={`pres-file${item.expiring ? ' pres-file--expiring' : ''}`}>
                                    <FileIcon />
                                    <div className="pres-file__info">
                                        <b>{item.title}</b>
                                        <span>
                                            {TYPE_LABELS[item.file_type]} · {formatSize(item.size)} · загружен {formatDate(item.created_at)}
                                        </span>
                                        <span className="pres-file__days">
                                            {item.days_left > 0
                                                ? `Удалится через ${item.days_left} ${daysWord(item.days_left)} — ${formatDate(item.expires_at)}`
                                                : 'Удалится сегодня'}
                                        </span>
                                        {item.status === 'checking' && (
                                            <span className="pres-status pres-status--checking">Проверяется — обычно до минуты. Письма уйдут после проверки</span>
                                        )}
                                        {item.status === 'review' && (
                                            <span className="pres-status pres-status--review">На проверке у модератора — пока не рассылается</span>
                                        )}
                                        <span className="pres-file__stats">
                                            Запросили {item.stats.requests} · отправлено {item.stats.sent} · скачали {item.stats.downloads}
                                        </span>
                                        {item.used_in?.length > 0 ? (
                                            <ul className="pres-used">
                                                {item.used_in.map((u) => (
                                                    <li key={`${u.kind}-${u.id}`}>
                                                        <a href={u.kind === 'poll' ? `/dashboard?edit=${u.id}` : `/qa-host/${u.id}`}>
                                                            {u.kind === 'poll' ? 'Опрос' : 'Вопросы из зала'} «{u.title}»
                                                        </a>
                                                        {!u.enabled && <span className="pres-used__off"> · «Поделиться» выключено</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="pres-used__none">Не подключён ни к одному опросу или сессии — выберите его в настройках опроса или «Вопросов из зала»</span>
                                        )}
                                    </div>
                                    <button type="button" className="pres-file__delete" onClick={() => remove(item)}>Удалить</button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {full ? (
                        <p className="pres-note">Можно хранить {limits.max_files} файла одновременно — удалите один, чтобы загрузить новый.</p>
                    ) : (
                        <>
                        <label className="pres-ack">
                            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                            <span>{data.ack_text}</span>
                        </label>
                        <div
                            className={`pres-drop${drag ? ' is-drag' : ''}${uploading ? ' is-busy' : ''}${accepted ? '' : ' is-locked'}`}
                            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                            onDragLeave={() => setDrag(false)}
                            onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
                        >
                            {uploading ? (
                                <>
                                    <b>Загружаем… {progress}%</b>
                                    <div className="pres-progress"><i style={{ width: `${progress}%` }} /></div>
                                </>
                            ) : (
                                <>
                                    <b>Перетащите файл сюда</b>
                                    <span>или</span>
                                    <button type="button" className="btn btn--primary btn--sm" disabled={!accepted} onClick={() => inputRef.current?.click()}>Выбрать файл</button>
                                    <small>PDF, PPTX или ODP до {Math.round(limits.max_bytes / MB)} МБ. Файл хранится {limits.keep_days} дней и удаляется сам.</small>
                                </>
                            )}
                            <input
                                ref={inputRef} type="file" hidden
                                accept=".pdf,.pptx,.odp,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.presentation"
                                onChange={(e) => upload(e.target.files?.[0])}
                            />
                        </div>
                        </>
                    )}
                    {data.blocked?.length > 0 && (
                        <ul className="pres-blocked">
                            {data.blocked.map((b) => (
                                <li key={b.id}>
                                    <b>«{b.title}» заблокирован модератором</b> {formatDate(b.blocked_at)}: {b.reason}. Файл удалён, участники его не получат.
                                    Если вы не согласны — ответьте на письмо о блокировке.
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="pres-muted pres-tip">
                        Совет: сохраните презентацию в PDF — она откроется на любом телефоне и будет весить меньше.
                        Не принимаются файлы с макросами, скриптами, встроенными объектами и программами.
                        {data.review_mode !== 'off' && ' Перед рассылкой файл проверяется автоматически.'}
                        {' '}Получатели могут пожаловаться на файл — тогда его посмотрит модератор.
                    </p>
                </section>

                <section className="pres-card">
                    <div className="pres-card__title"><h2>Статистика</h2></div>
                    <div className="pres-stats">
                        <div><b>{stats.total}</b><span>запросили</span></div>
                        <div><b>{stats.sent}</b><span>отправлено</span></div>
                        <div><b>{stats.waiting}</b><span>ждут файл</span></div>
                        <div><b>{stats.downloads}</b><span>скачали</span></div>
                        <div><b>{stats.registered}</b><span>зарегистрировались в СВОД</span></div>
                        <div><b>{stats.paid}</b><span>оплатили по промокоду</span></div>
                    </div>
                </section>

                <section className="pres-card">
                    <div className="pres-card__title">
                        <h2>Кто запросил</h2>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={exportEmails} disabled={!shared}>
                            Скачать почты ({shared})
                        </button>
                    </div>
                    <p className="pres-muted">
                        Полный адрес виден, только если участник отметил «Передать мой email организатору». Запросы хранятся 90 дней.
                    </p>
                    {requests.length === 0 ? (
                        <p className="pres-empty">Пока никто не запрашивал презентацию.</p>
                    ) : (
                        <div className="pres-table-wrap">
                            <table className="pres-table">
                                <thead><tr><th>Почта</th><th>Статус</th><th>Скачал</th><th>Когда</th></tr></thead>
                                <tbody>
                                    {requests.map((r) => (
                                        <tr key={r.id}>
                                            <td>{r.email}</td>
                                            <td>{STATUS[r.status] || r.status}</td>
                                            <td>{r.downloaded ? 'да' : '—'}</td>
                                            <td>{formatDate(r.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        );
    }

    return <AppShell section="presentations" title="Ваши презентации">{content}</AppShell>;
}

export default Presentations;
